import {
  BreakpointPixelValue,
  Breakpoints,
  Carousel,
  IconArrowRight,
  ProfileImage,
  Rating,
  RatingSize,
  useMediaQuery,
} from '@leland-dev/leland-ui-library';
import { type PortableTextComponent } from '@portabletext/react';
import groq from 'groq';
import { useEffect, useState } from 'react';

import { formatGoalCoachesLabel, formatName } from '../../../utils/formatters';
import { getCoachProfileUrl } from '../../../utils/routing';
import { sanityCategoryIdToUrn } from '../../../utils/sanity';

import {
  type TopCoachesCategoryFragment,
  type TopCoachesCoachFragment,
  useTopCoachesGetCoachesAndCategoryLazyQuery,
} from './__generated-gql-types__/TopCoaches.generated';

const TOP_COACHES_TYPE = 'topCoaches';
export const TOP_COACHES_MARK_DEFS_SELECTION = groq`_type == "${TOP_COACHES_TYPE}" => {
  _key,
  _type,
  category->{
    _id,
    "slug": slug.current,
  }
}`;

interface Props {
  _type: typeof TOP_COACHES_TYPE;
  category: {
    _id: string;
    slug: string;
  };
}

const TopCoaches: PortableTextComponent<Props> = ({
  value: {
    category: { _id: categoryId = '', slug: categorySlug = '' } = {},
  } = {},
}) => {
  const isSm = useMediaQuery(Breakpoints.SM);
  const isMd = useMediaQuery(Breakpoints.MD);
  const isXl = useMediaQuery(Breakpoints.XL);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [coaches, setCoaches] = useState<Nullable<TopCoachesCoachFragment[]>>();
  const [category, setCategory] =
    useState<Nullable<TopCoachesCategoryFragment>>();

  const [fetchCoachesAndCategory] =
    useTopCoachesGetCoachesAndCategoryLazyQuery();

  useEffect(() => {
    if (categoryId) {
      let isCancelled = false;
      void (async () => {
        setIsLoading(true);
        const { data, error } = await fetchCoachesAndCategory({
          variables: {
            categoryUrn: sanityCategoryIdToUrn(categoryId),
            offset: 0,
            count: 4,
          },
        });

        if (isCancelled) {
          return;
        }

        if (error) {
          setCoaches(null);
          setCategory(null);
        } else {
          setCoaches(
            data?.searchCoaches.results.length
              ? data.searchCoaches.results
              : null,
          );
          setCategory(data?.category);
        }
        setIsLoading(false);
      })();

      return () => {
        isCancelled = true;
        setIsLoading(false);
      };
    }
  }, [categoryId, fetchCoachesAndCategory]);

  if (!isLoading && !coaches) {
    return null;
  }

  return (
    <div className="my-6 md:my-10">
      <h2 className="mb-6 text-2xl font-medium">
        Top {category?.name} {formatGoalCoachesLabel(category?.goal)}
      </h2>
      {isLoading ? (
        <div className="my-4 flex w-full gap-6 md:my-10">
          <CoachCardSkeleton />
          {isSm ? <CoachCardSkeleton /> : null}
          {isMd ? <CoachCardSkeleton /> : null}
          {isXl ? <CoachCardSkeleton /> : null}
        </div>
      ) : (
        <Carousel
          swiperProps={{
            slidesPerView: 1.25,
            spaceBetween: 20,
            breakpoints: {
              [BreakpointPixelValue.SM]: {
                slidesPerView: 2,
              },
              [BreakpointPixelValue.MD]: {
                slidesPerView: 3,
              },
              [BreakpointPixelValue.XL]: {
                slidesPerView: 4,
              },
            },
          }}
          items={
            coaches?.map((coach) => (
              <CoachCard
                key={coach.id}
                coach={coach}
                categorySlug={categorySlug}
              />
            )) ?? []
          }
        />
      )}
    </div>
  );
};

interface CoachCardProps {
  coach: TopCoachesCoachFragment;
  categorySlug: string;
}

const CoachCard: React.FC<CoachCardProps> = ({ coach, categorySlug }) => (
  <div className="size-full pb-4">
    <a
      className="flex size-full flex-col items-start rounded-xl border border-leland-gray-stroke p-4 shadow-md"
      href={getCoachProfileUrl(coach.slug, categorySlug)}
      target="_blank"
      rel="noreferrer"
    >
      {coach.user.pictureLink ? (
        <ProfileImage {...coach.user} className="size-20" objectFit="cover" />
      ) : null}
      <div className="flex-1">
        <p className="mt-5 text-lg font-medium text-leland-gray-dark">
          {formatName({
            firstName: coach.user.firstName,
            lastName: coach.user.lastName,
          })}
        </p>
        <p className="line-clamp-2 text-base font-normal text-leland-gray-light">
          {coach.categoryInformation?.headline}
        </p>
        {coach.reviewTotal > 0 ? (
          <div className="mt-2 flex items-center space-x-1">
            <Rating
              rate={coach.averageReviewRate}
              showRate
              size={RatingSize.SMALL}
            />
            <p className="font-normal text-leland-gray-light">
              ({coach.reviewTotal})
            </p>
          </div>
        ) : null}
      </div>
      <div className="link link--primary mt-2 flex items-center justify-start space-x-1 hover:space-x-1.5">
        <p className="link text-base font-normal leading-none">
          View coach profile
        </p>
        <IconArrowRight className="size-3" />
      </div>
    </a>
  </div>
);

const CoachCardSkeleton: React.FC = () => {
  return (
    <div className="flex w-full animate-pulse flex-col items-start justify-start">
      <div className="size-20 shrink-0 rounded-full bg-gray-200" />
      <div className="mt-5 flex w-full flex-col items-start justify-center">
        <div className="h-6 w-1/2 rounded-sm bg-gray-200" />
        <div className="mt-1 h-8 w-3/4 rounded-sm bg-gray-200" />
        <div className="mt-2 h-4 w-1/2 rounded-sm bg-gray-200" />
        <div className="mt-2 h-4 w-3/5 rounded-sm bg-gray-200" />
      </div>
    </div>
  );
};

export default TopCoaches;
